import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

const VIEW_SCOPE_PREFIX = 'isfet-admin-view-scope:'

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null)
	const [loading, setLoading] = useState(true)
	const [isAdmin, setIsAdmin] = useState(false)
	const [customerId, setCustomerId] = useState(null)
	const [hasCompanyAssignment, setHasCompanyAssignment] = useState(false)
	const [brandingEnabled, setBrandingEnabled] = useState(true)
	const [customLogo, setCustomLogo] = useState(null)
	const [roleCheckComplete, setRoleCheckComplete] = useState(false)
	const [pendingPasswordReset, setPendingPasswordReset] = useState(false)
	const [viewScope, setViewScopeState] = useState(null)
	const isMountedRef = useRef(true)
	const currentRoleCheckIdRef = useRef(0)
	const initAuthCompletedRef = useRef(false)
	const lastUserIdRef = useRef(null)
	const roleCacheRef = useRef({})

	const checkUserRole = useCallback(async (email, userId, checkId, isStillValid, cache, options = {}) => {
		const { bypassCache = false } = options

		// Check cache first - if we have role data for this user, use it immediately
		if (!bypassCache && userId && cache[userId]) {
			const cached = cache[userId]
			setIsAdmin(cached.isAdmin)
			setCustomerId(cached.customerId)
			setHasCompanyAssignment(cached.hasCompanyAssignment)
			setBrandingEnabled(cached.brandingEnabled)
			setCustomLogo(cached.customLogo ?? null)
			setRoleCheckComplete(true)
			return
		}
		
		setRoleCheckComplete(false)
		
		// Timeout to ensure role check always completes
		const timeoutId = setTimeout(() => {
			if (isStillValid()) {
				console.error('Role check timed out')
				setRoleCheckComplete(true)
			}
		}, 10000)
		
		try {
			if (!isStillValid()) {
				clearTimeout(timeoutId)
				return
			}

			// Check if user is admin by querying admin_users table
			let isAdminUser = false
			const { data: adminData } = await supabase
				.from('admin_users')
				.select('email')
				.eq('email', email)
				.maybeSingle()
			
			if (!isStillValid()) {
				clearTimeout(timeoutId)
				return
			}
			
			if (adminData?.email) {
				isAdminUser = true
			}
			
			// Check for customer assignment regardless of admin status
			// This allows admins to also have customer access
			let foundCustomerId = null
			
			if (userId) {
				// Look up customer assignment in user_customers table
				const { data: mappingData } = await supabase
					.from('user_customers')
					.select('customer_id')
					.eq('user_id', userId)
					.maybeSingle()

				if (!isStillValid()) {
					clearTimeout(timeoutId)
					return
				}

				if (mappingData?.customer_id) {
					foundCustomerId = mappingData.customer_id
				}
			}

			// Legacy fallback: look up customer by email in customers.name column
			if (!foundCustomerId) {
				const { data: customerData } = await supabase
					.from('customers')
					.select('id')
					.eq('name', email)
					.maybeSingle()

				if (!isStillValid()) {
					clearTimeout(timeoutId)
					return
				}

				if (customerData?.id) {
					foundCustomerId = customerData.id
				}
			}

			// Set role data based on both admin status and customer assignment
			let resolvedBrandingEnabled = true
			let resolvedCustomLogo = null

			if (foundCustomerId) {
				const { data: customerSettings } = await supabase
					.from('customers')
					.select('show_branding, custom_logo')
					.eq('id', foundCustomerId)
					.maybeSingle()

				if (!isStillValid()) {
					clearTimeout(timeoutId)
					return
				}

				if (typeof customerSettings?.show_branding === 'boolean') {
					resolvedBrandingEnabled = customerSettings.show_branding
				}
				if (typeof customerSettings?.custom_logo === 'string' && customerSettings.custom_logo.trim()) {
					resolvedCustomLogo = customerSettings.custom_logo.trim()
				}
			}

			const roleData = {
				isAdmin: isAdminUser,
				customerId: foundCustomerId,
				hasCompanyAssignment: !!foundCustomerId,
				brandingEnabled: resolvedBrandingEnabled,
				customLogo: resolvedCustomLogo
			}
			
			setIsAdmin(roleData.isAdmin)
			setCustomerId(roleData.customerId)
			setHasCompanyAssignment(roleData.hasCompanyAssignment)
			setBrandingEnabled(roleData.brandingEnabled)
			setCustomLogo(roleData.customLogo)
			setRoleCheckComplete(true)
			if (userId) cache[userId] = roleData
			clearTimeout(timeoutId)
		} catch (error) {
			console.error('Error checking user role:', error)
			if (isStillValid()) {
				setIsAdmin(false)
				setCustomerId(null)
				setHasCompanyAssignment(false)
				setBrandingEnabled(true)
				setCustomLogo(null)
				setRoleCheckComplete(true)
			}
			clearTimeout(timeoutId)
		}
	}, [])

	const refreshAuthState = useCallback(async () => {
		if (!user?.id || !user?.email) {
			setIsAdmin(false)
			setCustomerId(null)
			setHasCompanyAssignment(false)
			setBrandingEnabled(true)
			setCustomLogo(null)
			setRoleCheckComplete(true)
			return
		}

		const checkId = ++currentRoleCheckIdRef.current
		await checkUserRole(
			user.email,
			user.id,
			checkId,
			() => isMountedRef.current && currentRoleCheckIdRef.current === checkId,
			roleCacheRef.current,
			{ bypassCache: true }
		)
	}, [checkUserRole, user])

	useEffect(() => {
		isMountedRef.current = true

		const initAuth = async () => {
			try {
				const { data: { session } } = await supabase.auth.getSession()

				if (!isMountedRef.current) return

				setUser(session?.user ?? null)
				lastUserIdRef.current = session?.user?.id ?? null

				if (session?.user) {
					const checkId = ++currentRoleCheckIdRef.current
					await checkUserRole(
						session.user.email,
						session.user.id,
						checkId,
						() => isMountedRef.current && currentRoleCheckIdRef.current === checkId,
						roleCacheRef.current
					)
				} else {
					setRoleCheckComplete(true)
				}
			} catch (error) {
				console.error('Error initializing auth:', error)
				if (isMountedRef.current) {
					setRoleCheckComplete(true)
				}
			} finally {
				if (isMountedRef.current) {
					setLoading(false)
					initAuthCompletedRef.current = true
				}
			}
		}

		initAuth()

		// Listen for auth changes (sign in/out)
		const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
			if (!isMountedRef.current) return

			// When a password recovery link is clicked, Supabase auto-authenticates
			// the user. Flag this so the layout hides authenticated UI.
			// This must be checked BEFORE the initAuthCompleted gate below,
			// because the PASSWORD_RECOVERY event fires during the initial load.
			if (event === 'PASSWORD_RECOVERY') {
				setPendingPasswordReset(true)
			}

			// Don't process auth state changes until initAuth completes
			// This prevents race conditions on page load
			if (!initAuthCompletedRef.current) return

			// Only process actual sign in/out events, ignore token refresh and other events
			// that don't change the user
			const newUserId = session?.user?.id ?? null
			const userChanged = newUserId !== lastUserIdRef.current

			// Only re-check role if user actually changed (sign in/out)
			if (!userChanged && event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') {
				return
			}

			lastUserIdRef.current = newUserId

			// Increment check ID to invalidate any ongoing role checks
			currentRoleCheckIdRef.current++

			setUser(session?.user ?? null)
			if (session?.user) {
				const checkId = currentRoleCheckIdRef.current
				await checkUserRole(
					session.user.email,
					session.user.id,
					checkId,
					() => isMountedRef.current && currentRoleCheckIdRef.current === checkId,
					roleCacheRef.current
				)
			} else {
				setIsAdmin(false)
				setCustomerId(null)
				setHasCompanyAssignment(false)
				setBrandingEnabled(true)
				setCustomLogo(null)
				setRoleCheckComplete(true)
			}
		})

		return () => {
			isMountedRef.current = false
			subscription.unsubscribe()
		}
	}, [checkUserRole])

	const signIn = async (email, password) => {
		const { data, error } = await supabase.auth.signInWithPassword({
			email,
			password
		})
		return { data, error }
	}

	const signUp = async (email, password) => {
		const { data, error } = await supabase.auth.signUp({
			email,
			password
		})
		return { data, error }
	}

	const signOut = async () => {
		const { error } = await supabase.auth.signOut()
		return { error }
	}

	const resetPassword = async (email) => {
		const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
			redirectTo: `${window.location.origin}/reset-password`,
		})
		return { data, error }
	}

	const updatePassword = async (newPassword) => {
		const { data, error } = await supabase.auth.updateUser({
			password: newPassword,
		})
		return { data, error }
	}

	const clearPasswordReset = () => setPendingPasswordReset(false)

	// Load stored view scope from localStorage when the user changes
	useEffect(() => {
		if (!user?.id) {
			setViewScopeState(null)
			return
		}
		try {
			const stored = localStorage.getItem(`${VIEW_SCOPE_PREFIX}${user.id}`)
			setViewScopeState(stored === 'all' ? 'all' : null)
		} catch {
			setViewScopeState(null)
		}
	}, [user?.id])

	// Clear scope if the user loses admin status
	useEffect(() => {
		if (!isAdmin && viewScope !== null) {
			setViewScopeState(null)
		}
	}, [isAdmin, viewScope])

	const setViewScope = useCallback((next) => {
		if (!isAdmin || !user?.id) return
		const value = next === 'all' ? 'all' : null
		setViewScopeState(value)
		try {
			if (value) {
				localStorage.setItem(`${VIEW_SCOPE_PREFIX}${user.id}`, value)
			} else {
				localStorage.removeItem(`${VIEW_SCOPE_PREFIX}${user.id}`)
			}
		} catch {
			// ignore quota / private mode
		}
	}, [isAdmin, user?.id])

	const isAllScope = isAdmin && viewScope === 'all'
	const effectiveCustomerId = isAllScope ? null : customerId

	return (
		<AuthContext.Provider value={{
			user,
			loading,
			isAdmin,
			customerId,
			effectiveCustomerId,
			hasCompanyAssignment,
			brandingEnabled,
			customLogo,
			roleCheckComplete,
			pendingPasswordReset,
			viewScope,
			isAllScope,
			setViewScope,
			signIn,
			signUp,
			signOut,
			resetPassword,
			updatePassword,
			clearPasswordReset,
			refreshAuthState,
		}}>
			{children}
		</AuthContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext)
